import { useEffect, useMemo, useState } from 'react'
import GShell from './GShell.jsx'
import MemberPicker, { useMembersMap, memberName } from './MemberPicker.jsx'
import { Title, Card, Pill, Modal, Field, Err, money, fmtDate } from './gui.jsx'
import { RENTAL_ITEMS, watchRentals, createRental, checkOutRental, returnRental, deleteRental, isRentalOverdue } from '../../lib/rentals.js'

export default function GRentals() {
  const [raw, setRaw] = useState({})
  const [tab, setTab] = useState('Out')
  const [retFor, setRetFor] = useState(null)
  const members = useMembersMap()
  useEffect(() => { const off = watchRentals(setRaw); return () => off() }, [])

  const rows = useMemo(() => Object.values(raw || {}).filter(Boolean).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)), [raw])
  const byStatus = (s) => rows.filter((r) => r.status === s)
  const list = byStatus(tab)

  return (
    <GShell>
      <section>
        <Title title="Rentals" subtitle="Reserve, check out, and return rental gear." />
        <div className="mt-3"><RentalForm /></div>
      </section>

      <div className="flex flex-wrap gap-2">
        {['Reserved', 'Out', 'Returned'].map((s) => (
          <button key={s} onClick={() => setTab(s)} className="text-xs font-bold px-3 py-1.5 rounded-full"
            style={tab === s ? { background: 'var(--ge-primary)', color: '#fff' } : { background: 'rgba(0,0,0,0.05)', color: '#4B5563' }}>{s} {byStatus(s).length}</button>
        ))}
      </div>

      <section className="pb-4">
        {list.length === 0 ? <p className="text-sm text-gray-500">Nothing here.</p> : (
          <div className="grid md:grid-cols-2 gap-3">
            {list.map((r) => {
              const od = isRentalOverdue(r)
              return (
                <Card key={r.id} style={od ? { borderColor: '#F9731655' } : {}}>
                  <div className="flex items-start justify-between gap-2">
                    <div><div className="font-extrabold" style={{ color: 'var(--ge-primary)' }}>{r.itemType}</div>
                      <div className="text-sm text-gray-700">{memberName(members, r.memberId)}</div></div>
                    {od ? <Pill bg="rgba(249,115,22,0.16)" fg="#C2410C">Overdue</Pill> : <Pill>{r.status}</Pill>}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3">
                    {r.outDate ? <span>Out {fmtDate(r.outDate)}</span> : null}{r.dueDate ? <span>Due {fmtDate(r.dueDate)}</span> : null}
                    {r.fees ? <span>💵 {money(r.fees)}</span> : null}{r.damage ? <span>⚠ {r.damage}</span> : null}
                  </div>
                  {r.notes ? <p className="text-xs text-gray-600 mt-1">{r.notes}</p> : null}
                  <div className="mt-3 flex items-center gap-2 pt-2 border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                    {r.status === 'Reserved' ? <button className="btn-primary text-xs" onClick={() => checkOutRental(r.id, r.itemType)}>Check Out</button> : null}
                    {r.status === 'Out' ? <button className="btn-primary text-xs" onClick={() => setRetFor(r)}>Return</button> : null}
                    <button className="btn-ghost text-xs ml-auto" style={{ color: '#B91C1C' }} onClick={async () => { if (window.confirm(`Delete ${r.itemType} rental?`)) await deleteRental(r.id) }}>Delete</button>
                  </div>
                </Card>
              )
            })}
          </div>
        )}
      </section>

      {retFor ? <ReturnModal r={retFor} onClose={() => setRetFor(null)} /> : null}
    </GShell>
  )
}

function ReturnModal({ r, onClose }) {
  const [damage, setDamage] = useState(r.damage || '')
  const [fees, setFees] = useState(String(r.fees ?? ''))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const save = async () => { setBusy(true); setErr(''); try { await returnRental(r.id, { damage, fees }, r.memberId, r.itemType); onClose() } catch (e) { setErr(e?.message || 'Failed'); setBusy(false) } }
  return (
    <Modal title={`Return — ${r.itemType}`} onClose={onClose}>
      <div className="grid sm:grid-cols-2 gap-2">
        <Field label="Damage / condition"><input className="input w-full" value={damage} onChange={(e) => setDamage(e.target.value)} /></Field>
        <Field label="Fees $"><input className="input w-full" type="number" step="0.01" value={fees} onChange={(e) => setFees(e.target.value)} /></Field>
      </div>
      <div className="flex justify-end gap-2 mt-3"><button className="btn-ghost text-sm" onClick={onClose}>Cancel</button><button className="btn-primary text-sm" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Mark Returned'}</button></div>
      <Err msg={err} />
    </Modal>
  )
}

function RentalForm() {
  const empty = { memberId: '', itemType: RENTAL_ITEMS[0], outDate: '', dueDate: '', fees: '', notes: '' }
  const [f, setF] = useState(empty)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }))
  const submit = async (asRes) => { setBusy(true); setErr(''); try { await createRental(f, asRes); setF(empty) } catch (e) { setErr(e?.message || 'Failed') } finally { setBusy(false) } }
  return (
    <Card>
      <div className="text-xs font-extrabold uppercase tracking-widest mb-2" style={{ color: 'var(--ge-primary)' }}>New Rental</div>
      <div className="mb-2"><MemberPicker value={f.memberId} onChange={(v) => setF((s) => ({ ...s, memberId: v }))} /></div>
      <div className="grid sm:grid-cols-2 gap-2">
        <select className="input" value={f.itemType} onChange={set('itemType')}>{RENTAL_ITEMS.map((i) => <option key={i}>{i}</option>)}</select>
        <input className="input" type="number" step="0.01" placeholder="Fee $" value={f.fees} onChange={set('fees')} />
        <input className="input" type="date" value={f.outDate} onChange={set('outDate')} title="Out date" />
        <input className="input" type="date" value={f.dueDate} onChange={set('dueDate')} title="Due date" />
      </div>
      <input className="input mt-2 w-full" placeholder="Notes" value={f.notes} onChange={set('notes')} />
      <div className="flex gap-2 mt-2">
        <button className="btn-primary text-sm" onClick={() => submit(false)} disabled={busy}>Check Out Now</button>
        <button className="btn-secondary text-sm" onClick={() => submit(true)} disabled={busy}>Reserve</button>
      </div>
      <Err msg={err} />
    </Card>
  )
}
